import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import client from '@/api/client';
import { setAuthToken, setRefreshCallback } from '@/api/client';
import { exchangeEdcToken } from '@/api/sso';
import {
  consumePendingEdcToken,
  installEdcPostMessageBridge,
  purgeUserScopedState,
  rememberSessionUserId,
  maskToken,
} from '@/auth/edcTokenBridge';

/**
 * Origins allowed to hand CTMS an EDC token via postMessage. Comma-separated
 * in VITE_EDC_ORIGIN. Empty (the default) disables the postMessage path
 * entirely — the `?token=` handoff still works, and iframe embedding is
 * opt-in rather than open to any window that can reach us.
 */
const EDC_ALLOWED_ORIGINS = (import.meta.env.VITE_EDC_ORIGIN ?? '')
  .split(',')
  .map((o: string) => o.trim())
  .filter(Boolean);

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  /** Derived display name — the API returns firstName/lastName, never `name`. */
  name: string;
  role: string;
  permissions: string[];
  studyAssignments: string[];
}

/** Raw user shape returned by /auth/login, /auth/me and /auth/sso/edc. */
interface ApiUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  permissions?: string[];
  studyAssignments?: string[];
}

/**
 * Normalise the API user into the context's shape. Single definition shared
 * by the password-login, session-restore and EDC-SSO paths so a federated
 * user is never represented differently from a password user.
 */
function toAuthUser(raw: ApiUser): AuthUser {
  const firstName = raw.firstName ?? '';
  const lastName = raw.lastName ?? '';
  return {
    id: raw.id,
    email: raw.email,
    firstName,
    lastName,
    name: `${firstName} ${lastName}`.trim() || raw.email,
    role: raw.role,
    permissions: raw.permissions ?? [],
    studyAssignments: raw.studyAssignments ?? [],
  };
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<string | null>;
  /** True when this session was established by an EDC handoff, not a password. */
  isEdcSession: boolean;
}

// ─── Context ────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ─── Provider ───────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    accessToken: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isEdcSession, setIsEdcSession] = useState(false);

  // ── Helpers ───────────────────────────────────────────────────────────────

  /** Schedule a token refresh a bit before the token expires. */
  const scheduleRefresh = useCallback((expiresInMs: number) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    // Refresh 60 seconds before expiry (minimum 10 s)
    const delay = Math.max(expiresInMs - 60_000, 10_000);
    refreshTimerRef.current = setTimeout(() => {
      refreshTokenFn();
    }, delay);
  }, []);

  /** Parse JWT payload to read exp claim (seconds). */
  function getTokenExpiresIn(token: string): number {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp as number; // epoch seconds
      return exp * 1000 - Date.now();
    } catch {
      return 15 * 60 * 1000; // fallback 15 min
    }
  }

  // ── Refresh token ─────────────────────────────────────────────────────────

  const refreshTokenFn = useCallback(async (): Promise<string | null> => {
    try {
      // The backend should accept the refresh token from httpOnly cookie
      // or from localStorage fallback
      const storedRefresh = localStorage.getItem('ctms_refresh_token');
      const { data } = await client.post('/auth/refresh', {
        refreshToken: storedRefresh ?? undefined,
      });

      const newAccessToken: string = data.accessToken;
      const newRefreshToken: string | undefined = data.refreshToken;

      // Store tokens
      setAuthToken(newAccessToken);
      if (newRefreshToken) {
        localStorage.setItem('ctms_refresh_token', newRefreshToken);
      }

      setState((prev) => ({
        ...prev,
        accessToken: newAccessToken,
        isAuthenticated: true,
      }));

      scheduleRefresh(getTokenExpiresIn(newAccessToken));

      return newAccessToken;
    } catch {
      // Refresh failed - clear everything
      setAuthToken(null);
      localStorage.removeItem('ctms_refresh_token');
      setState({
        user: null,
        accessToken: null,
        isAuthenticated: false,
        isLoading: false,
      });
      return null;
    }
  }, [scheduleRefresh]);

  // ── Register refresh callback with the axios client ───────────────────────

  useEffect(() => {
    setRefreshCallback(refreshTokenFn);
    return () => setRefreshCallback(null);
  }, [refreshTokenFn]);

  // ── Session establishment (shared by password login and EDC SSO) ──────────

  /**
   * Commit an authenticated session from a login/exchange response. Both entry
   * points funnel through here so a federated session is stored, scheduled and
   * tracked exactly like a password session.
   */
  const establishSession = useCallback(
    (
      data: { accessToken: string; refreshToken?: string; user: ApiUser },
      source: 'password' | 'edc-sso',
    ): void => {
      const user = toAuthUser(data.user);

      // Access token stays in memory only; the refresh token is the sole
      // persisted credential.
      setAuthToken(data.accessToken);
      if (data.refreshToken) {
        localStorage.setItem('ctms_refresh_token', data.refreshToken);
      }
      // Record whose session this is, so a later EDC handoff for a DIFFERENT
      // user can detect the switch and purge before seeding.
      rememberSessionUserId(user.id);

      setIsEdcSession(source === 'edc-sso');
      setState({
        user,
        accessToken: data.accessToken,
        isAuthenticated: true,
        isLoading: false,
      });

      scheduleRefresh(getTokenExpiresIn(data.accessToken));
    },
    [scheduleRefresh],
  );

  // ── Login ─────────────────────────────────────────────────────────────────

  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      const { data } = await client.post('/auth/login', { email, password });
      establishSession(data, 'password');
    },
    [establishSession],
  );

  // ── EDC single sign-on ────────────────────────────────────────────────────

  /**
   * Exchange a Wave EDC handoff token for a native CTMS session. Returns true
   * on success; on failure the caller falls back to the normal login flow —
   * a bad handoff must never leave a half-established session behind.
   */
  const loginWithEdcToken = useCallback(
    async (edcToken: string): Promise<boolean> => {
      try {
        const data = await exchangeEdcToken(edcToken);
        establishSession(data, 'edc-sso');
        console.info('[auth] EDC SSO session established', {
          edcToken: maskToken(edcToken),
          user: data.user.email,
          role: data.user.role,
        });
        return true;
      } catch (err) {
        // Purge rather than fall through: whatever session existed was already
        // invalidated by the bridge if the user differed, and keeping a
        // partially-applied state here is the stale-user bug.
        purgeUserScopedState('EDC SSO exchange failed');
        setAuthToken(null);
        setIsEdcSession(false);
        console.warn('[auth] EDC SSO exchange failed — falling back to login', err);
        return false;
      }
    },
    [establishSession],
  );

  // ── Logout ────────────────────────────────────────────────────────────────

  const logout = useCallback(async (): Promise<void> => {
    try {
      await client.post('/auth/logout');
    } catch {
      // Ignore logout failures
    }

    setAuthToken(null);
    // Drops the refresh token, the remembered email, the session-user marker
    // and the React Query cache — nothing fetched under this session may
    // survive into the next user's.
    purgeUserScopedState('logout');

    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    setIsEdcSession(false);
    setState({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, []);

  // ── On mount: validate existing session ──────────────────────────────────

  useEffect(() => {
    let cancelled = false;

    async function init() {
      // 1. An EDC handoff wins over any existing session. bootstrapEdcUrlTokenSync
      //    (main.tsx) captured the token before first render and already purged
      //    the previous user's state if the identity changed — so consuming it
      //    here is the first thing we do, ahead of the refresh-token path.
      const edcToken = consumePendingEdcToken();
      if (edcToken) {
        const ok = await loginWithEdcToken(edcToken);
        if (cancelled) return;
        if (ok) return; // establishSession already cleared isLoading
        // Exchange failed — fall through to the normal session-restore path,
        // which will land on /login if there is nothing to restore.
      }

      // 2. No handoff: restore the persisted session as before.
      const storedRefresh = localStorage.getItem('ctms_refresh_token');
      if (!storedRefresh) {
        setState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        // Try to refresh the access token
        const newAccessToken = await refreshTokenFn();
        if (cancelled || !newAccessToken) return;

        // Temporarily set token for /auth/me call
        setAuthToken(newAccessToken);

        const { data: raw } = await client.get<ApiUser>('/auth/me');
        if (cancelled) return;

        const user = toAuthUser(raw);
        rememberSessionUserId(user.id);
        setState({
          user,
          accessToken: newAccessToken,
          isAuthenticated: true,
          isLoading: false,
        });
      } catch {
        if (cancelled) return;
        setAuthToken(null);
        localStorage.removeItem('ctms_refresh_token');
        setState({
          user: null,
          accessToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Late-arriving EDC tokens (iframe embedding, EDC-side user switch) ─────

  useEffect(() => {
    if (EDC_ALLOWED_ORIGINS.length === 0) return;
    return installEdcPostMessageBridge((token) => {
      void loginWithEdcToken(token);
    }, EDC_ALLOWED_ORIGINS);
  }, [loginWithEdcToken]);

  // ── Cleanup timer on unmount ──────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    refreshToken: refreshTokenFn,
    isEdcSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
